import { useEffect } from "react";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import Lenis from "lenis";
import "lenis/dist/lenis.css";
import "@/App.css";
import { Toaster } from "@/components/ui/sonner";
import { Nav } from "@/components/lunelle/Nav";
import { Hero } from "@/components/lunelle/Hero";
import { Marquee } from "@/components/lunelle/Marquee";
import { Phases } from "@/components/lunelle/Phases";
import { Kitchen } from "@/components/lunelle/Kitchen";
import { Pillars } from "@/components/lunelle/Pillars";
import { Demo } from "@/components/lunelle/Demo";
import { FinalCta } from "@/components/lunelle/FinalCta";
import { Footer } from "@/components/lunelle/Footer";
import { TodayWord } from "@/components/lunelle/TodayWord";
import { ZodiacStyle } from "@/components/lunelle/ZodiacStyle";
import { Budget } from "@/components/lunelle/Budget";
import { LifestyleIndex } from "@/components/lunelle/LifestyleIndex";
import { CycleCalendar } from "@/components/lunelle/CycleCalendar";
import TodayPage from "@/pages/TodayPage";
import PrivacyPage from "@/pages/PrivacyPage";
import UnsubscribePage from "@/pages/UnsubscribePage";
import SpotPage from "@/pages/SpotPage";
import ConfirmPage from "@/pages/ConfirmPage";
import AdminPage from "@/pages/AdminPage";
import PressPage from "@/pages/PressPage";
import ResetPasswordPage from "@/pages/ResetPasswordPage";
import DailyEmailOffPage from "@/pages/DailyEmailOffPage";
import CreatorsPage from "@/pages/CreatorsPage";
import CreatorLinkPage from "@/pages/CreatorLinkPage";
import { AccountProvider } from "@/lib/account";
import { AuthCallback } from "@/components/account/AuthCallback";
import { AccountDialog } from "@/components/account/AccountDialog";
import { CycleSync } from "@/components/account/CycleSync";
import { ProfileDialog } from "@/components/account/ProfileDialog";
import { usePageMeta } from "@/lib/usePageMeta";
import { goTo, captureRef } from "@/lib/api";
import { toast } from "sonner";

const Home = () => {
  const { hash } = useLocation();
  useEffect(() => {
    if (!captureRef()) return undefined;
    const t = setTimeout(() => toast("A friend invited you to Lunelle", { description: "Join the waitlist below and you'll both be founding members." }), 1600);
    return () => clearTimeout(t);
  }, []);
  usePageMeta("Lunelle — Cycle & Lifestyle App for Women | Food, Faith & Style", "Lunelle is a cycle and lifestyle app for women: track every phase, eat for your body type, get a daily word from your faith, style for your sign and budget.");
  useEffect(() => {
    window.scrollTo(0, 0);
    if (!hash) return undefined;
    const t = setTimeout(() => goTo(hash.slice(1)), 400);
    return () => clearTimeout(t);
  }, [hash]);
  return (
    <>
      <Nav />
      <main>
        <Hero />
        <Marquee />
        <LifestyleIndex />
        <Phases />
        <Kitchen />
        <Pillars />
        <ZodiacStyle />
        <Budget />
        <TodayWord />
        <Demo />
        <FinalCta />
        <CycleCalendar />
      </main>
      <Footer />
    </>
  );
};

const AppRoutes = () => {
  const location = useLocation();
  // Google sign-in returns with #session_id=...; handle it before any route renders
  if (location.hash?.includes("session_id=")) return <AuthCallback />;
  return (
    <Routes>
      <Route path="/" element={<Home />} />
      <Route path="/today" element={<TodayPage />} />
      <Route path="/privacy" element={<PrivacyPage />} />
      <Route path="/unsubscribe" element={<UnsubscribePage />} />
      <Route path="/spot" element={<SpotPage />} />
      <Route path="/confirm" element={<ConfirmPage />} />
      <Route path="/admin" element={<AdminPage />} />
      <Route path="/press" element={<PressPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      <Route path="/daily-email-off" element={<DailyEmailOffPage />} />
      <Route path="/creators" element={<CreatorsPage />} />
      <Route path="/c/:code" element={<CreatorLinkPage />} />
    </Routes>
  );
};

function App() {
  useEffect(() => {
    const lenis = new Lenis({ duration: 1.2, smoothWheel: true });
    window.__lenis = lenis;
    let id = requestAnimationFrame(function raf(t) {
      lenis.raf(t);
      id = requestAnimationFrame(raf);
    });
    return () => {
      cancelAnimationFrame(id);
      lenis.destroy();
      window.__lenis = null;
    };
  }, []);

  return (
    <div className="lunelle grain overflow-x-clip bg-[#FDFBF7] font-sans text-[#2C221E]">
      <BrowserRouter>
        <AccountProvider>
          <AppRoutes />
          <AccountDialog />
          <CycleSync />
          <ProfileDialog />
        </AccountProvider>
      </BrowserRouter>
      <Toaster position="bottom-center" />
    </div>
  );
}

export default App;
